import { Route as RRDRoute } from 'react-router-dom';
import { getQueryVariable } from '@sda/utils/url';
import { ComponentWrapper } from './Routes/ComponentWrapper';
import { DumbWrapper } from './Routes/DumbWrapper';
import type { ProfileTypes } from '@sda/enums';
import type { RouteProps as RRDRouteProps } from 'react-router-dom';

type RouteProps<C = React.ElementType> = RRDRouteProps<string> & {
  readonly load?: React.LazyExoticComponent<React.FC<any>>;
  readonly component?: C;
  readonly ComponentProps?: C extends React.ElementType ? React.ComponentPropsWithRef<C> : never;
};

type ProfileRouteProps<C = React.ElementType> = RouteProps<C> & {
  readonly profileType?: boolean | ProfileTypes;
};

export const Route = <C extends React.ElementType>({
  path, component, load, ComponentProps, ...rest
}: RouteProps<C>) => (
  <RRDRoute
    path={path}
    {...rest}
    render={((props) => {
      const secure = getQueryVariable('secure');

      if (secure !== 'true') {
        return (
          <DumbWrapper
            path={path}
            component={component}
            load={load}
            {...props}
            {...ComponentProps || {}}
          />
        );
      }

      return (
        <ComponentWrapper
          path={path}
          component={component}
          load={load}
          {...props}
          {...ComponentProps || {}}
          requiresLogin={secure === 'true' ? true : null}
        />
      );
    })}
  />
  );

export const NoLoginRoute = <C extends React.ElementType>({
  path, component, load, ComponentProps, ...rest
}: RouteProps<C>) => (
  <RRDRoute
    path={path}
    {...rest}
    render={((props) => (
      <ComponentWrapper
        path={path}
        component={component}
        load={load}
        {...props}
        {...ComponentProps || {}}
        requiresLogin={false}
      />
    ))}
  />
  );

export const ProtectedRoute = <C extends React.ElementType>({
  path, component, load, ComponentProps, ...rest
}: RouteProps<C>) => (
  <RRDRoute
    path={path}
    {...rest}
    render={((props) => (
      <ComponentWrapper
        path={path}
        component={component}
        load={load}
        {...props}
        {...ComponentProps || {}}
        requiresLogin
      />
    ))}
  />
  );

export const ProfileRoute = <C extends React.ElementType>({
  path, component, load, ComponentProps, profileType, ...rest
}: ProfileRouteProps<C>) => (
  <RRDRoute
    path={path}
    {...rest}
    render={((props) => (
      <ComponentWrapper
        path={path}
        component={component}
        load={load}
        {...props}
        {...ComponentProps || {}}
        requiresProfile={profileType}
        requiresLogin
      />
    ))}
  />
  );

export const AdminRoute = <C extends React.ElementType>({
  path, component, load, ComponentProps, ...rest
}: RouteProps<C>) => (
  <RRDRoute
    path={path}
    {...rest}
    render={((props) => (
      <ComponentWrapper
        path={path}
        component={component}
        load={load}
        {...props}
        {...ComponentProps || {}}
        requiresLogin
        adminOnly
      />
    ))}
  />
  );
